import { apiUrl } from "../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

import DataGridTable from "../Components/DataGridTable";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import NotificationDetailsPopup from "../Components/NotificationDetailsPopup";
import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import systemNotificationsTranslations from "../languages/systemNotifications.json";
import {
  formatSystemNotificationTimestamp,
  localizeSystemNotification,
} from "../utils/systemNotificationText";
import "./AdminTablePages.css";

export const SystemNotifications = () => {
  const { lang } = useContext(LanguageContext);
  const t = systemNotificationsTranslations[lang] || systemNotificationsTranslations.en;
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const autoMarkedOnOpenRef = useRef(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: ["ar", "ur"].includes(lang) ? "left" : "right",
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: ["ar", "ur"].includes(lang) ? "left" : "right",
    });
  }, [lang]);

  const filterOptions = useMemo(
    () => [
      { value: "all", label: t.filterOptions.all },
      { value: "unread", label: t.filterOptions.unread },
      { value: "read", label: t.filterOptions.read },
    ],
    [t]
  );

  const mapNotificationsToRows = useCallback((items) => {
    return (Array.isArray(items) ? items : []).map((n) => {
      const localized = localizeSystemNotification(n, t);

      return {
        id: n.id,
        created_at: n.created_at,
        title: localized.title || "",
        message: localized.message || "",
        type: n.type || "",
        status: n.status || "",
        subid: n.subid || null,
        device_name: n?.device?.name || "",
        did: n?.device?.did || "",
        owner_name: n?.device?.owner?.name || n?.device?.owner?.email || "",
        raw: localized,
      };
    });
  }, [t]);

  const normalizeIds = useCallback(
    (items) =>
      Array.from(
        new Set(
          (Array.isArray(items) ? items : [])
            .map((item) => Number(item))
            .filter((item) => Number.isInteger(item) && item > 0)
        )
      ),
    []
  );

  const applyStatusLocally = useCallback(
    (ids, nextStatus) => {
      const idSet = new Set(normalizeIds(ids));

      setRows((prev) => {
        const updated = prev.map((row) =>
          idSet.has(Number(row.id)) ? { ...row, status: nextStatus } : row
        );

        if (filter === "unread") {
          return updated.filter(
            (row) => String(row.status || "").trim().toLowerCase() === "unread"
          );
        }
        if (filter === "read") {
          return updated.filter(
            (row) => String(row.status || "").trim().toLowerCase() === "read"
          );
        }
        return updated;
      });

      setSelectedRows((prev) =>
        prev.filter((rowId) => !idSet.has(Number(rowId)))
      );
      setSelectedNotification((prev) =>
        prev && idSet.has(Number(prev.id)) ? { ...prev, status: nextStatus } : prev
      );
    },
    [filter, normalizeIds]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(apiUrl(`device/plan-notifications`), {
        params: { status: filter },
      });
      setRows(mapNotificationsToRows(res?.data?.data));
    } catch (err) {
      showAlert("error", getErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
      setHasFetchedOnce(true);
    }
  }, [filter, mapNotificationsToRows, showAlert]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const updateNotificationsStatus = useCallback(
    async (ids, nextStatus, options = {}) => {
      const normalizedIds = normalizeIds(ids);
      const statusValue = nextStatus === "unread" ? "unread" : "read";

      if (!normalizedIds.length) {
        if (!options.silent) {
          showAlert("warning", t.alerts.selectOne);
        }
        return false;
      }

      setActionLoading(statusValue);
      try {
        await axios.post(
          apiUrl(`device/plan-notifications/status`),
          { ids: normalizedIds, status: statusValue },
          { headers: { "Content-Type": "application/json" } }
        );

        applyStatusLocally(normalizedIds, statusValue);
        if (!options.silent) {
          showAlert("info", statusValue === "unread" ? t.alerts.markedUnread : t.alerts.markedRead);
        }
        return true;
      } catch (err) {
        if (!options.silent) {
          showAlert("error", getErrorMessage(err));
        }
        return false;
      } finally {
        setActionLoading("");
      }
    },
    [applyStatusLocally, normalizeIds, showAlert, t.alerts.markedRead, t.alerts.markedUnread, t.alerts.selectOne]
  );

  useEffect(() => {
    setSelectedRows((prev) =>
      prev.filter((rowId) => rows.some((row) => Number(row.id) === Number(rowId)))
    );
  }, [rows]);

  useEffect(() => {
    if (!hasFetchedOnce || loading || autoMarkedOnOpenRef.current) return;

    autoMarkedOnOpenRef.current = true;
    const unreadVisibleIds = rows
      .filter((row) => String(row.status || "").trim().toLowerCase() === "unread")
      .map((row) => row.id);

    if (!unreadVisibleIds.length) return;
    void updateNotificationsStatus(unreadVisibleIds, "read", { silent: true });
  }, [hasFetchedOnce, loading, rows, updateNotificationsStatus]);

  const markAsRead = useCallback(
    async (row) => {
      if (!row || String(row.status || "").toLowerCase() !== "unread") return;
      await updateNotificationsStatus([row.id], "read");
    },
    [updateNotificationsStatus]
  );

  const markAsUnread = useCallback(
    async (row) => {
      if (!row || String(row.status || "").toLowerCase() !== "read") return;
      await updateNotificationsStatus([row.id], "unread");
    },
    [updateNotificationsStatus]
  );

  const markAllVisibleAsRead = useCallback(async () => {
    const unreadVisibleIds = rows
      .filter((row) => String(row.status || "").trim().toLowerCase() === "unread")
      .map((row) => row.id);

    if (!unreadVisibleIds.length) {
      showAlert("info", t.alerts.allVisibleAlreadyRead);
      return;
    }
    await updateNotificationsStatus(unreadVisibleIds, "read");
  }, [rows, showAlert, t.alerts.allVisibleAlreadyRead, updateNotificationsStatus]);

  const handleRowClick = useCallback((params) => {
    const row = params?.row;
    if (!row) return;
    setSelectedNotification(row.raw || row);
  }, []);

  const closeNotificationDetails = useCallback(() => {
    setSelectedNotification(null);
  }, []);

  const markSelectedAsRead = useCallback(async () => {
    if (!selectedNotification) return;
    await updateNotificationsStatus([selectedNotification.id], "read");
  }, [selectedNotification, updateNotificationsStatus]);

  const markSelectedAsUnread = useCallback(async () => {
    if (!selectedNotification) return;
    await updateNotificationsStatus([selectedNotification.id], "unread");
  }, [selectedNotification, updateNotificationsStatus]);

  const selectActions = useMemo(
    () => [
      {
        title: t.selectActions.markSelectedAsRead,
        icon: (
          <DoneOutlinedIcon style={{ color: "rgb(12, 198, 12)", marginInlineEnd: 12 }} />
        ),
        action: () => updateNotificationsStatus(selectedRows, "read"),
      },
      {
        title: t.selectActions.markSelectedAsUnread,
        icon: (
          <MarkEmailUnreadOutlinedIcon
            style={{ color: "rgb(240, 182, 57)", marginInlineEnd: 12 }}
          />
        ),
        action: () => updateNotificationsStatus(selectedRows, "unread"),
      },
    ],
    [selectedRows, t.selectActions.markSelectedAsRead, t.selectActions.markSelectedAsUnread, updateNotificationsStatus]
  );

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: t.columns.actions,
        type: "actions",
        width: 120,
        getActions: (params) =>
          [
            String(params.row.status || "").toLowerCase() === "unread" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.markAsRead}>
                    <DoneOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.markAsRead}
                onClick={() => markAsRead(params.row)}
              />
            ) : (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.markAsUnread}>
                    <MarkEmailUnreadOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.markAsUnread}
                onClick={() => markAsUnread(params.row)}
              />
            ),
          ].filter(Boolean),
      },
      { field: "id", headerName: t.columns.id, width: 80, align: "center", headerAlign: "center" },
      {
        field: "created_at",
        headerName: t.columns.createdAt,
        width: 190,
        align: "center",
        headerAlign: "center",
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        valueFormatter: (params) =>
          formatSystemNotificationTimestamp(params?.value ?? params, lang),
      },
      { field: "title", headerName: t.columns.title, width: 260, align: "left", headerAlign: "left" },
      { field: "message", headerName: t.columns.message, width: 420, align: "left", headerAlign: "left" },
      { field: "type", headerName: t.columns.type, width: 150, align: "center", headerAlign: "center" },
      {
        field: "status",
        headerName: t.columns.status,
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const statusValue = String(params.row?.status || "").toLowerCase();
          return (
            <span
              className={`admin-table-status${
                statusValue === "unread" ? " unread" : " read"
              }`}
            >
              {statusValue === "unread" ? t.statuses.unread : t.statuses.read}
            </span>
          );
        },
      },
      { field: "device_name", headerName: t.columns.device, width: 190, align: "left", headerAlign: "left" },
      { field: "owner_name", headerName: t.columns.owner, width: 190, align: "left", headerAlign: "left" },
      { field: "did", headerName: t.columns.did, width: 160, align: "center", headerAlign: "center" },
      { field: "subid", headerName: t.columns.subId, width: 90, align: "center", headerAlign: "center" },
    ],
    [lang, markAsRead, markAsUnread, t]
  );

  const unreadVisibleCount = rows.filter(
    (row) => String(row.status || "").trim().toLowerCase() === "unread"
  ).length;

  return (
    <div className="configuration devices-page admin-table-page">
      <Alert
        open={alertState.open}
        close={() => setAlertState((s) => ({ ...s, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{t.title}</h2>
          </div>
          <div className="admin-table-header-actions">
            <button
              type="button"
              className="devices-secondary-btn admin-table-action-btn"
              onClick={fetchRows}
              disabled={loading || actionLoading !== ""}
            >
              <RefreshIcon fontSize="small" />
              <span>{t.refresh}</span>
            </button>
            <button
              type="button"
              className="devices-primary-btn admin-table-action-btn"
              onClick={markAllVisibleAsRead}
              disabled={loading || actionLoading !== ""}
            >
              <MarkEmailReadOutlinedIcon fontSize="small" />
              <span>{t.markAllAsRead}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : rows.length > 0 ? (
          <div className="admin-table-grid">
            <DataGridTable
              rows={rows}
              columns={columns}
              handleRowClick={handleRowClick}
              idcolumn="id"
              pagingoptions={[10, 20, 50, 100]}
              sortField="id"
              sortType="desc"
              checkboxSelection={true}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="system_notifications"
              toolbarPrefix={
                <div className="admin-table-toolbar-left">
                  <div className="admin-table-filter-wrap">
                    <span className="admin-table-filter-label">{t.filterLabel}</span>
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="admin-table-filter"
                      disabled={loading || actionLoading !== ""}
                    >
                      {filterOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-table-meta">
                    {t.unreadCount.replace("{count}", unreadVisibleCount)}
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <div className="admin-table-empty">{t.empty}</div>
        )}
      </div>

      <NotificationDetailsPopup
        open={Boolean(selectedNotification)}
        notification={selectedNotification}
        onClose={closeNotificationDetails}
        onMarkRead={markSelectedAsRead}
        onMarkUnread={markSelectedAsUnread}
      />
    </div>
  );
};

export default SystemNotifications;
